import { useState, useEffect } from 'react'

import {
  Drawer,
  TextInput,
  Select,
  MultiSelect,
  Divider,
  Text,
  ActionIcon,
  Badge,
  Loader,
  Tooltip,
  Menu,
} from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import Button from 'src/components/Buttons/Button/Button'
import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import { RoleType } from 'src/graphql/types/employees'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_DOCUMENT_TEMPLATE_FIELDS,
  REFRESH_DOCUMENT_TEMPLATE_FIELDS,
  UPDATE_DOCUMENT_TEMPLATE_FIELD,
} from 'src/graphql/documentTemplateFields.graphql'
import { ADD_DOCUMENT_TEMPLATE } from 'src/graphql/documentTemplatePackages.graphql'
import {
  GET_DOCUMENT_TEMPLATES,
  UPDATE_DOCUMENT_TEMPLATE,
} from 'src/graphql/documentTemplates.graphql'
import {
  DocumentCategory,
  DocumentCategoryLabel,
  DocumentUserStatus,
} from 'src/graphql/types/documents'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconLeftArrow from 'src/icons/IconLeftArrow'
import IconRefresh from 'src/icons/IconRefresh'
import { formatSnakeValue } from 'src/lib/formatters'

const signerOptions = [
  { value: 'APPLICANT', label: 'Applicant' },
  { value: 'RECRUITER', label: 'Employee' },
]

interface DocumentTemplateDrawerProps {
  opened: boolean
  onClose: () => void
  templateId: string | null
  mode: 'create' | 'edit'
  onBack?: () => void
}

const DocumentTemplateDrawer = ({
  opened,
  onClose,
  templateId,
  mode,
  onBack,
}: DocumentTemplateDrawerProps) => {
  const { currentUser } = useAuth()
  // OPS users have same permissions as ADMIN in this context
  const isAdmin = isAdminOrOps(currentUser)

  const [fieldSearch, setFieldSearch] = useState('')
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null)
  const [editingFieldType, setEditingFieldType] = useState<
    'fieldName' | 'fieldLabel' | null
  >(null)
  const [editingValue, setEditingValue] = useState('')

  const form = useForm({
    initialValues: {
      documentName: '',
      signingToolRefId: '',
      documentCategory: '',
      signers: [] as string[],
      userStatus: '',
      status: '',
    },
  })

  const { data: templatesData, loading: templatesLoading } = useQuery(
    GET_DOCUMENT_TEMPLATES,
    {
      skip: !templateId || mode === 'create',
    }
  )

  const {
    data: fieldsData,
    loading: fieldsLoading,
    refetch: refetchFields,
  } = useQuery(GET_DOCUMENT_TEMPLATE_FIELDS, {
    variables: { documentTemplateId: templateId },
    skip: !templateId || mode === 'create',
    fetchPolicy: 'network-only',
  })

  const [createTemplate, { loading: creating }] = useMutation(
    ADD_DOCUMENT_TEMPLATE,
    {
      refetchQueries: [GET_DOCUMENT_TEMPLATES],
      onError: (error) => {
        console.error('Create template error:', error)
        toast(error.message || 'Unable to create template', 'error')
      },
      onCompleted: (data) => {
        const name = data?.addDocumentTemplate?.documentName || 'Template'
        toast(`"${name}" created`, 'success')
        onClose()
      },
    }
  )

  const [updateTemplate, { loading: updating }] = useMutation(
    UPDATE_DOCUMENT_TEMPLATE,
    {
      refetchQueries: [GET_DOCUMENT_TEMPLATES],
      onError: (error) => toast(error.message, 'error'),
      onCompleted: (data) => {
        const name = data?.updateDocumentTemplate?.documentName || 'Template'
        toast(`"${name}" updated`, 'success')
        onClose()
      },
    }
  )

  const [refreshFields, { loading: refreshing }] = useMutation(
    REFRESH_DOCUMENT_TEMPLATE_FIELDS,
    {
      onCompleted: () => {
        toast('Fields refreshed', 'success')
        refetchFields()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const [updateField] = useMutation(UPDATE_DOCUMENT_TEMPLATE_FIELD, {
    onCompleted: () => {
      toast('Field updated', 'success')
      refetchFields()
      setEditingFieldId(null)
      setEditingFieldType(null)
      setEditingValue('')
    },
    onError: (error) => toast(error.message, 'error'),
  })

  // Find the template from the list
  const template = templatesData?.documentTemplates?.find(
    (t) => t.documentTemplateId === templateId
  )

  // Populate form when template data is loaded or set defaults for create mode
  useEffect(() => {
    if (mode === 'create' && opened) {
      form.setValues({
        documentName: '',
        signingToolRefId: '',
        documentCategory: '',
        signers: ['APPLICANT'],
        userStatus: 'PENDING_REVIEW',
        status: 'ACTIVE',
      })
    } else if (template) {
      form.setValues({
        documentName: template.documentName || '',
        signingToolRefId: template.signingToolRefId || '',
        documentCategory: template.documentCategory || '',
        signers: template.signers || [],
        userStatus: template.userStatus || '',
        status: template.status || '',
      })
    }
  }, [template, mode, opened])

  // Reset form when drawer closes
  useEffect(() => {
    if (!opened) {
      form.reset()
      setFieldSearch('')
      setEditingFieldId(null)
      setEditingFieldType(null)
      setEditingValue('')
    }
  }, [opened])

  const handleSave = () => {
    if (mode === 'create') {
      if (!form.values.signingToolRefId?.trim()) {
        toast('Anvil ID is required', 'error')
        return
      }
      if (!form.values.documentCategory) {
        toast('Category is required', 'error')
        return
      }

      createTemplate({
        variables: {
          input: {
            status: 'ACTIVE',
            signingTool: 'ANVIL',
            signingToolRefId: form.values.signingToolRefId.trim(),
            signers: form.values.signers,
            // Let backend use Anvil title as default if no name provided
            documentName: form.values.documentName?.trim() || '',
            documentCategory: form.values.documentCategory,
            userStatus: form.values.userStatus || 'PENDING_REVIEW',
          },
        },
      })
    } else {
      if (!templateId) return

      updateTemplate({
        variables: {
          documentTemplateId: templateId,
          input: {
            documentName: form.values.documentName,
            documentCategory: form.values.documentCategory,
            signers: form.values.signers,
            userStatus: form.values.userStatus,
            status: form.values.status,
          },
        },
      })
    }
  }

  const handleRefreshFields = () => {
    if (!templateId) return

    // If no fields exist yet, we need to use the template ID to create them
    if (!fieldsData?.documentTemplateFields?.[0]) {
      // TODO: Need backend mutation to create fields by templateId
      refetchFields()
      return
    }

    refreshFields({
      variables: {
        documentTemplateFieldId:
          fieldsData.documentTemplateFields[0].documentTemplateFieldId,
      },
    })
  }

  const handleStartEditing = (
    fieldId: string,
    type: 'fieldName' | 'fieldLabel',
    currentValue: string
  ) => {
    setEditingFieldId(fieldId)
    setEditingFieldType(type)
    setEditingValue(currentValue || '')
  }

  const handleSaveFieldEdit = (fieldId: string) => {
    if (!editingFieldType) return

    updateField({
      variables: {
        documentTemplateFieldId: fieldId,
        input: {
          [editingFieldType]: editingValue,
        },
      },
    })
  }

  const handleCancelEdit = () => {
    setEditingFieldId(null)
    setEditingFieldType(null)
    setEditingValue('')
  }

  const handleUpdateFieldRequired = (fieldId: string, isRequired: boolean) => {
    updateField({
      variables: {
        documentTemplateFieldId: fieldId,
        input: { isRequired },
      },
    })
  }

  const handleUpdateFieldSigners = (fieldId: string, signers: string[]) => {
    updateField({
      variables: {
        documentTemplateFieldId: fieldId,
        input: { signers },
      },
    })
  }

  const handleUpdateFieldType = (fieldId: string, fieldType: string) => {
    updateField({
      variables: {
        documentTemplateFieldId: fieldId,
        input: { fieldType },
      },
    })
  }

  const getSignerLabel = (signers: string[]) => {
    if (!signers?.length) return 'No signer'
    if (signers.length === 2) return 'Both'
    return signers[0] === 'APPLICANT' ? 'Applicant' : 'Employee'
  }

  const getSignerColor = (signers: string[]) => {
    if (!signers?.length) return 'gray'
    if (signers.length === 2) return 'violet'
    return signers[0] === 'APPLICANT' ? 'blue' : 'green'
  }

  const fieldTypeOptions = [
    { value: 'SHORT_TEXT', label: 'Short Text' },
    { value: 'CHECKBOX', label: 'Checkbox' },
    { value: 'RADIO_GROUP', label: 'Radio Group' },
    { value: 'INTEGER', label: 'Integer' },
    { value: 'DOLLAR', label: 'Dollar' },
    { value: 'DATE', label: 'Date' },
    { value: 'SIGNATURE', label: 'Signature' },
    { value: 'SIGNATURE_DATE', label: 'Signature Date' },
    { value: 'INITIAL', label: 'Initial' },
  ]

  const fields = fieldsData?.documentTemplateFields || []
  const filteredFields = fields.filter(
    (field) =>
      field.fieldName?.toLowerCase().includes(fieldSearch.toLowerCase()) ||
      field.fieldLabel?.toLowerCase().includes(fieldSearch.toLowerCase())
  )

  const loading = templatesLoading || updating || creating

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      withCloseButton={false}
      size="30%"
      offset={6}
      radius="md"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        body: 'flex flex-col h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {onBack && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size={24}
                onClick={onBack}
              >
                <IconLeftArrow className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
              </ActionIcon>
            )}
            <h2 className="text-base font-semibold text-doubleNickel-gray-900">
              {mode === 'create' ? 'Create Template' : 'Edit Template'}
            </h2>
          </div>
          <Button
            text={mode === 'create' ? 'Create' : 'Save'}
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={loading}
          />
        </div>

        <Divider variant="dashed" />

        {loading && mode === 'edit' && !template ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto">
            {/* Overview Section */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                OVERVIEW
              </Text>

              <div className="space-y-3">
                {mode === 'create' && isAdmin && (
                  <div className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Text size="xs">Anvil ID</Text>
                      <Tooltip label="Admin only">
                        <div className="h-1.5 w-1.5 rounded-full bg-doubleNickel-error-400" />
                      </Tooltip>
                    </div>
                    <div className="w-[250px]">
                      <TextInput
                        size="xs"
                        classNames={{ input: 'text-xs' }}
                        placeholder="Enter Anvil template ID"
                        {...form.getInputProps('signingToolRefId')}
                      />
                    </div>
                  </div>
                )}

                {mode === 'edit' && isAdmin && template?.signingToolRefId && (
                  <div className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Text size="xs">Anvil ID</Text>
                      <Tooltip label="Admin only">
                        <div className="h-1.5 w-1.5 rounded-full bg-doubleNickel-error-400" />
                      </Tooltip>
                    </div>
                    <div className="flex w-[250px] items-center gap-1">
                      <Text
                        size="xs"
                        className="truncate text-doubleNickel-gray-600"
                      >
                        {template.signingToolRefId}
                      </Text>
                      <CopyableId
                        value={template.signingToolRefId}
                        label="Anvil ID"
                      />
                    </div>
                  </div>
                )}

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Name</Text>
                  <div className="w-[250px]">
                    <TextInput
                      size="xs"
                      classNames={{ input: 'text-xs' }}
                      placeholder={
                        mode === 'create'
                          ? 'Optional - uses Anvil template name'
                          : ''
                      }
                      {...form.getInputProps('documentName')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Category</Text>
                  <div className="w-[250px]">
                    <Select
                      size="xs"
                      searchable
                      classNames={{ input: 'text-xs' }}
                      data={Object.values(DocumentCategory).map((value) => ({
                        value,
                        label: DocumentCategoryLabel[value],
                      }))}
                      {...form.getInputProps('documentCategory')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Signers</Text>
                  <div className="w-[250px]">
                    <MultiSelect
                      size="xs"
                      classNames={{ input: 'text-xs' }}
                      data={signerOptions}
                      {...form.getInputProps('signers')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Review Status</Text>
                  <div className="w-[250px]">
                    <Select
                      size="xs"
                      classNames={{ input: 'text-xs' }}
                      data={Object.values(DocumentUserStatus).map((value) => ({
                        value,
                        label: formatSnakeValue(value),
                      }))}
                      {...form.getInputProps('userStatus')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Fields Section - visible in edit mode for all users */}
            {mode === 'edit' && (
              <>
                <Divider variant="dashed" />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Text
                      size="xs"
                      fw={400}
                      className="text-doubleNickel-gray-500"
                    >
                      FIELDS
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size={24}
                      onClick={handleRefreshFields}
                      loading={refreshing}
                      title="Refresh fields from Anvil"
                    >
                      <IconRefresh className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                    </ActionIcon>
                  </div>

                  {fields.length > 0 && (
                    <TextInput
                      placeholder="Search fields by name or label..."
                      size="xs"
                      classNames={{ input: 'text-xs' }}
                      value={fieldSearch}
                      onChange={(e) => setFieldSearch(e.target.value)}
                    />
                  )}

                  {fieldsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader size="xs" color="gray" />
                    </div>
                  ) : filteredFields.length === 0 ? (
                    <div className="py-2 text-center">
                      <Text size="xs" className="text-doubleNickel-gray-400">
                        {fields.length === 0
                          ? 'No fields found. Fields are created when this template is added to an e-signature package.'
                          : 'No fields match your search'}
                      </Text>
                    </div>
                  ) : (
                    <div className="space-y-2 overflow-y-auto">
                      {filteredFields.map((field) => (
                        <div
                          key={field.documentTemplateFieldId}
                          className="rounded-md border border-doubleNickel-gray-200 p-2"
                        >
                          {/* Row 1: Label and type/required */}
                          <div className="flex items-center justify-between">
                            {editingFieldId === field.documentTemplateFieldId &&
                            editingFieldType === 'fieldLabel' ? (
                              <TextInput
                                size="xs"
                                classNames={{ input: 'text-xs' }}
                                value={editingValue}
                                onChange={(e) =>
                                  setEditingValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveFieldEdit(
                                      field.documentTemplateFieldId
                                    )
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit()
                                  }
                                }}
                                onBlur={() =>
                                  handleSaveFieldEdit(
                                    field.documentTemplateFieldId
                                  )
                                }
                                autoFocus
                                className="mr-2 flex-1"
                              />
                            ) : (
                              <Tooltip
                                label={field.fieldLabel || field.fieldName}
                                withArrow
                              >
                                <Text
                                  size="xs"
                                  fw={500}
                                  className="max-w-[180px] cursor-pointer truncate hover:text-doubleNickel-brand-500 hover:underline"
                                  onClick={() =>
                                    handleStartEditing(
                                      field.documentTemplateFieldId,
                                      'fieldLabel',
                                      field.fieldLabel
                                    )
                                  }
                                >
                                  {field.fieldLabel || field.fieldName}
                                </Text>
                              </Tooltip>
                            )}
                            <div className="flex shrink-0 items-center gap-1">
                              {isAdmin ? (
                                <Menu shadow="md" position="bottom-end">
                                  <Menu.Target>
                                    <Badge
                                      size="xs"
                                      variant="light"
                                      color="gray"
                                      className="cursor-pointer"
                                      rightSection={
                                        <IconChevronDown className="h-2.5 w-2.5 fill-none stroke-current" />
                                      }
                                    >
                                      {formatSnakeValue(field.fieldType)}
                                    </Badge>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    {fieldTypeOptions.map((option) => (
                                      <Menu.Item
                                        key={option.value}
                                        className="text-xs"
                                        onClick={() =>
                                          handleUpdateFieldType(
                                            field.documentTemplateFieldId,
                                            option.value
                                          )
                                        }
                                      >
                                        {option.label}
                                      </Menu.Item>
                                    ))}
                                  </Menu.Dropdown>
                                </Menu>
                              ) : (
                                <Badge size="xs" variant="light" color="gray">
                                  {formatSnakeValue(field.fieldType)}
                                </Badge>
                              )}
                              <Menu shadow="md" position="bottom-end">
                                <Menu.Target>
                                  <Badge
                                    size="xs"
                                    variant="light"
                                    color={field.isRequired ? 'red' : 'gray'}
                                    className="cursor-pointer"
                                    rightSection={
                                      <IconChevronDown className="h-2.5 w-2.5 fill-none stroke-current" />
                                    }
                                  >
                                    {field.isRequired ? 'Required' : 'Optional'}
                                  </Badge>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Item
                                    className="text-xs"
                                    onClick={() =>
                                      handleUpdateFieldRequired(
                                        field.documentTemplateFieldId,
                                        true
                                      )
                                    }
                                  >
                                    Required
                                  </Menu.Item>
                                  <Menu.Item
                                    className="text-xs"
                                    onClick={() =>
                                      handleUpdateFieldRequired(
                                        field.documentTemplateFieldId,
                                        false
                                      )
                                    }
                                  >
                                    Optional
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            </div>
                          </div>
                          {/* Row 2: Field name and signers */}
                          <div className="mt-1 flex items-center justify-between">
                            {editingFieldId === field.documentTemplateFieldId &&
                            editingFieldType === 'fieldName' ? (
                              <TextInput
                                size="xs"
                                classNames={{ input: 'text-xs' }}
                                value={editingValue}
                                onChange={(e) =>
                                  setEditingValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveFieldEdit(
                                      field.documentTemplateFieldId
                                    )
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit()
                                  }
                                }}
                                onBlur={() =>
                                  handleSaveFieldEdit(
                                    field.documentTemplateFieldId
                                  )
                                }
                                autoFocus
                                className="mr-2 flex-1"
                              />
                            ) : (
                              <Tooltip
                                label={field.fieldName}
                                withArrow
                              >
                                <Text
                                  size="xs"
                                  className="max-w-[180px] cursor-pointer truncate text-doubleNickel-gray-500 hover:text-doubleNickel-brand-500 hover:underline"
                                  onClick={() =>
                                    handleStartEditing(
                                      field.documentTemplateFieldId,
                                      'fieldName',
                                      field.fieldName
                                    )
                                  }
                                >
                                  {field.fieldName}
                                </Text>
                              </Tooltip>
                            )}
                            <Menu shadow="md" position="bottom-end">
                              <Menu.Target>
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color={getSignerColor(field.signers)}
                                  className="cursor-pointer"
                                  rightSection={
                                    <IconChevronDown className="h-2.5 w-2.5 fill-none stroke-current" />
                                  }
                                >
                                  {getSignerLabel(field.signers)}
                                </Badge>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  className="text-xs"
                                  onClick={() =>
                                    handleUpdateFieldSigners(
                                      field.documentTemplateFieldId,
                                      ['APPLICANT']
                                    )
                                  }
                                >
                                  Applicant
                                </Menu.Item>
                                <Menu.Item
                                  className="text-xs"
                                  onClick={() =>
                                    handleUpdateFieldSigners(
                                      field.documentTemplateFieldId,
                                      ['RECRUITER']
                                    )
                                  }
                                >
                                  Employee
                                </Menu.Item>
                                <Menu.Item
                                  className="text-xs"
                                  onClick={() =>
                                    handleUpdateFieldSigners(
                                      field.documentTemplateFieldId,
                                      ['APPLICANT', 'RECRUITER']
                                    )
                                  }
                                >
                                  Both
                                </Menu.Item>
                                <Menu.Item
                                  className="text-xs"
                                  onClick={() =>
                                    handleUpdateFieldSigners(
                                      field.documentTemplateFieldId,
                                      []
                                    )
                                  }
                                >
                                  None
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default DocumentTemplateDrawer
